/**
 * theme.js — loaded with `defer`, so it never blocks parsing or painting.
 *
 * Everything here is an enhancement. With JavaScript disabled the drawers
 * still open (they are <details>), the carousels still swipe (native
 * overflow scroll), the announcement bar shows its first message, and every
 * form posts normally. Nothing below is required to shop the store.
 */
(function () {
  'use strict';

  var reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * An observer root covering only the band ABOVE the viewport: the top edge
   * is pushed far up, the bottom edge pulled up by the full viewport height.
   * An element intersects it exactly when it has scrolled off the top.
   *
   * This is deliberately a state rather than a crossing. With a plain
   * `threshold: 0` an element reads as "not intersecting" both above and
   * below the viewport, so jumping between those two — an anchor link,
   * restored scroll position, `scrollIntoView` — changes nothing the
   * observer will report, and the callback never fires.
   */
  var ABOVE_VIEWPORT = { rootMargin: '9999px 0px -100% 0px' };

  /**
   * Run something exactly once per page, however many times `init` is called.
   *
   * `init` re-runs on every `shopify:section:load`, which the theme editor
   * fires on each edit. Anything bound to `document` rather than to a section
   * element would otherwise stack a fresh copy each time — two listeners
   * meant two cart requests per quantity change, which is how this was found.
   */
  var boundOnce = {};

  /**
   * Pending "Copied" resets, keyed by their button. A WeakMap rather than a
   * property on the element so a button removed by a section reload takes
   * its entry with it.
   */
  var restoreTimers = new WeakMap();

  /**
   * Zoom viewer constants. 2.6x is enough to read a fabric weave on a phone
   * without the picture turning to pixels; 6px is the distance that tells a
   * drag apart from a slightly unsteady tap.
   */
  var ZOOM_SCALE = 2.6;
  var PAN_THRESHOLD = 6;

  /**
   * One controller per viewer, so re-opening a dialog does not stack a
   * second set of listeners on it. A WeakMap rather than a property, so a
   * dialog removed by a section reload takes its entry with it.
   */
  var zoomControllers = new WeakMap();

  function once(key, bind) {
    if (boundOnce[key]) return;
    boundOnce[key] = true;
    bind();
  }

  /* ---------------------------------------------------- sticky header */

  /**
   * Flip the transparent overlay header to solid once it sticks. Uses an
   * IntersectionObserver on a 1px sentinel rather than a scroll listener,
   * so it contributes nothing to INP.
   */
  function initStickyHeader() {
    var sentinel = document.querySelector('[data-header-sentinel]');
    var header = document.querySelector('[data-header]');
    if (!sentinel || !header) return;

    new IntersectionObserver(function (entries) {
      header.classList.toggle('is-solid', entries[0].isIntersecting);
    }, ABOVE_VIEWPORT).observe(sentinel);
  }

  /**
   * Scroll lock.
   *
   * critical.css does the real work: `overflow: hidden` on html AND body
   * while a `[scroll-lock]` panel is open. That holds a mouse wheel
   * everywhere and a touch drag on Android — but root overflow has never
   * been reliable against touch on iOS Safari, and measurement showed one
   * gesture can still slip through before the lock latches.
   *
   * So this is a belt, not the trousers: while a panel is open, any scroll
   * that does get through is put straight back. With no script at all the
   * CSS still locks the page on every browser that honours it.
   */
  function initScrollLock() {
    // init re-runs after a section reload; a second observer and a second
    // scroll listener would fight over the same position.
    once('scrollLock', bindScrollLock);
  }

  function bindScrollLock() {
    var pinned = null;

    function openPanel() {
      return document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]');
    }

    function sync() {
      if (openPanel()) {
        // Only on the way in, so re-entering does not re-record a position
        // the shopper never scrolled to.
        if (pinned === null) pinned = window.scrollY;
      } else {
        pinned = null;
      }
    }

    document.addEventListener(
      'scroll',
      function () {
        if (pinned === null) return;

        /**
         * A panel can vanish instead of closing — a section re-render swaps
         * the header out, taking an open drawer with it — and then no
         * attribute ever changes for the observer to see. Re-checking here
         * means a stale pin can never leave the page unscrollable.
         */
        if (!openPanel()) {
          pinned = null;
          return;
        }

        // A sub-pixel difference is the browser settling, not a scroll.
        if (Math.abs(window.scrollY - pinned) < 1) return;
        window.scrollTo(0, pinned);
      },
      { passive: true }
    );

    /**
     * Watching the `open` attribute rather than wiring each opener: dialogs
     * and <details> drawers both carry it, and a panel opened by the theme
     * editor or by an app is caught the same way.
     */
    new MutationObserver(sync).observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });

    sync();
  }

  /**
   * Search popup.
   *
   * A native <dialog> opened with `showModal()`, which is what buys the top
   * layer, the backdrop, Escape-to-close and focus containment. Everything
   * here is an upgrade on markup that already works: the trigger is a link to
   * the search page, so with no script the shopper simply lands there.
   *
   * Bound once on the document, so re-running `init` after a section reload
   * cannot stack a second set of listeners.
   */
  function initSearchModal() {
    if (!document.querySelector('[data-search-modal]')) return;
    once('searchModal', bindSearchModal);
  }

  /**
   * Predictive search: results as the shopper types.
   *
   * The section is rendered by Liquid through the Predictive Search API, so
   * prices, translations and image sizing all come out right — building the
   * markup from the JSON endpoint would mean reimplementing `money` in
   * JavaScript.
   *
   * Everything here is an upgrade on a form that already works: with no
   * script, or if a request fails, submitting still lands on the search page.
   */
  function bindPredictiveSearch(dialog) {
    var timer = null;
    var controller = null;
    var lastQuery = '';

    function input() {
      return dialog.querySelector('[data-search-input]');
    }

    function results() {
      return dialog.querySelector('[data-search-results]');
    }

    function options() {
      var box = results();
      return box ? Array.prototype.slice.call(box.querySelectorAll('[data-psearch-option]')) : [];
    }

    function close() {
      var box = results();
      var field = input();
      if (box) {
        box.hidden = true;
        box.innerHTML = '';
      }
      if (field) {
        field.setAttribute('aria-expanded', 'false');
        field.removeAttribute('aria-activedescendant');
      }
    }

    /**
     * Marks one option as current. `aria-activedescendant` rather than moving
     * focus: the caret has to stay in the field so typing continues to work.
     */
    function highlight(index) {
      var list = options();
      var field = input();
      list.forEach(function (option, i) {
        var on = i === index;
        option.classList.toggle('is-current', on);
        option.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) {
          if (!option.id) option.id = 'psearch-option-' + i;
          if (field) field.setAttribute('aria-activedescendant', option.id);
          if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
        }
      });
      if (index < 0 && field) field.removeAttribute('aria-activedescendant');
    }

    function currentIndex() {
      var list = options();
      for (var i = 0; i < list.length; i++) {
        if (list[i].classList.contains('is-current')) return i;
      }
      return -1;
    }

    function run(query) {
      var box = results();
      if (!box) return;

      // One request at a time: an earlier, slower response landing after a
      // later one would show results for a query already typed past.
      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        shopRoot() +
        'search/suggest?q=' +
        encodeURIComponent(query) +
        '&section_id=predictive-search' +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (html === null) return;
          // A section response is the whole section; take what is inside it.
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var body = parsed.querySelector('.psearch, .psearch__empty');
          if (!body) {
            close();
            return;
          }
          /**
           * `importNode` rather than `appendChild` straight from the parsed
           * document. A node still owned by another Document cannot simply be
           * inserted here — the insert fails, and because the rejection lands
           * in the catch below it fails silently, leaving an empty box and no
           * clue why.
           */
          box.innerHTML = '';
          box.appendChild(document.importNode(body, true));
          box.hidden = false;

          // Freshly rendered by Liquid, so in the shop currency until the
          // converter app is run over it again.
          if (window.themeCurrency) window.themeCurrency.reconvert();
          var field = input();
          if (field) field.setAttribute('aria-expanded', 'true');
          highlight(-1);
        })
        .catch(function (error) {
          // A superseded request aborting is routine and not worth a word.
          if (error && error.name === 'AbortError') return;
          // Anything else: clear the box so no stale list is left behind, and
          // say so, because a silent catch here hid a real bug once already.
          close();
          if (window.console) console.warn('[theme] predictive search failed', error);
        });
    }

    dialog.addEventListener('input', function (event) {
      if (!event.target.matches('[data-search-input]')) return;

      var query = event.target.value.trim();
      if (timer) clearTimeout(timer);

      // Two characters is where suggestions start being worth the request.
      if (query.length < 2) {
        lastQuery = '';
        close();
        return;
      }

      if (query === lastQuery) return;
      lastQuery = query;

      timer = setTimeout(function () {
        run(query);
      }, 250);
    });

    /**
     * Arrow keys walk the list, Enter opens the highlighted one, Escape
     * closes the results before it closes the dialog — one press per layer,
     * which is what a shopper expects.
     */
    dialog.addEventListener('keydown', function (event) {
      if (!event.target.matches('[data-search-input]')) return;

      var list = options();

      if (event.key === 'Escape') {
        if (list.length) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        return;
      }

      if (!list.length) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        var next = currentIndex() + (event.key === 'ArrowDown' ? 1 : -1);
        if (next < 0) next = list.length - 1;
        if (next >= list.length) next = 0;
        highlight(next);
        return;
      }

      if (event.key === 'Enter') {
        var index = currentIndex();
        // Nothing highlighted means "search for what I typed" — let the form
        // submit as it always did.
        if (index < 0) return;
        event.preventDefault();
        list[index].click();
      }
    });

    // Closing the dialog should not leave stale results behind it.
    dialog.addEventListener('close', close);
  }

  function bindSearchModal() {
    // Looked up on demand: the theme editor can replace the header outright,
    // and a captured reference would be a detached node from then on.
    function modal() {
      return document.querySelector('[data-search-modal]');
    }

    var predictiveBound = false;
    function ensurePredictive(el) {
      if (predictiveBound || !el || !el.querySelector('[data-search-results]')) return;
      predictiveBound = true;
      bindPredictiveSearch(el);
    }

    document.addEventListener('click', function (event) {
      var opener = event.target.closest && event.target.closest('[data-search-open]');
      if (opener) {
        var el = modal();
        // No dialog support, or no dialog: let the link do its job.
        if (!el || typeof el.showModal !== 'function') return;

        event.preventDefault();
        ensurePredictive(el);
        el.showModal();

        // Focus the field rather than the dialog: the shopper opened this to
        // type. Deferred a frame, because a dialog that has only just entered
        // the top layer will not take focus yet.
        requestAnimationFrame(function () {
          var input = el.querySelector('[data-search-input]');
          if (input) input.focus();
        });
        return;
      }

      var closer = event.target.closest && event.target.closest('[data-search-close]');
      if (closer) {
        var open = modal();
        if (open && open.open) open.close();
        return;
      }

      /**
       * Clicking the backdrop closes it. The backdrop is not a separate
       * element, so a click that lands on the dialog itself rather than on
       * its panel is what identifies it.
       */
      var el2 = modal();
      if (!el2 || !el2.open || event.target !== el2) return;
      el2.close();
    });
  }

  /**
   * Cart attributes.
   *
   * Saved through `Shopify.actions.updateCart`, which writes the change and
   * emits `shopify:cart:attributes-update` for us. That is the whole reason
   * to go through the action rather than posting to /cart/update.js: apps
   * hear about the change without this theme dispatching anything, and there
   * is no risk of a listener seeing it twice.
   *
   * Two things the reference is explicit about, and both matter here:
   *
   *   - `attributes` is the COMPLETE set the cart should end up with. It
   *     replaces rather than merges, so every key the cart should keep has to
   *     be sent. Omitting one deletes it.
   *   - A rejected change resolves with `userErrors` instead of throwing, so
   *     that array has to be checked before treating it as saved.
   *
   * Without the action available — an older storefront, or a blocked script —
   * the textarea still carries a `name`, so the cart form submits it.
   */
  function initCartAttributes() {
    if (!document.querySelector('[data-cart-attr]')) return;
    once('cartAttributes', bindCartAttributes);
  }

  function bindCartAttributes() {
    var timer = null;

    /**
     * Read fresh each time rather than cached: the drawer's markup is
     * replaced wholesale on every refresh, so a captured element would be
     * detached and a cached string could outlive a locale change.
     */
    function labels() {
      var host = document.querySelector('[data-cart-attr-strings]');
      return {
        saved: (host && host.getAttribute('data-saved')) || '',
        error: (host && host.getAttribute('data-error')) || '',
      };
    }

    function status(field, message) {
      var el = field.closest('[data-cart-attrs]');
      var out = el && el.querySelector('[data-cart-attr-status]');
      if (out) out.textContent = message;
    }

/**
     * Every attribute the cart currently holds, read out of the JSON the
     * section renders. A global set by an inline script would go stale the
     * first time the drawer re-renders — `innerHTML` does not execute
     * scripts — so this reads the DOM, which the refresh does replace.
     */
    function known() {
      var host = document.querySelector('[data-cart-attrs-all]');
      if (!host) return {};
      try {
        return JSON.parse(host.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    /**
     * Read every attribute field on the page, so the set sent is complete.
     * `attributes` replaces rather than merges, so an attribute an app owns
     * and this theme has no field for still has to be sent back or it is
     * silently deleted.
     */
    function collect() {
      var out = {};
      var current = known();

      Object.keys(current).forEach(function (key) {
        out[key] = current[key];
      });

      Array.prototype.forEach.call(document.querySelectorAll('[data-cart-attr]'), function (field) {
        out[field.getAttribute('data-cart-attr')] = field.value;
      });

      return Object.keys(out).map(function (key) {
        return { key: key, value: out[key] };
      });
    }

    function save(field) {
      if (!window.Shopify || !Shopify.actions || !Shopify.actions.updateCart) return;

      var attributes = collect();
      field.setAttribute('aria-busy', 'true');

      Shopify.actions
        .updateCart({ attributes: attributes }, { context: 'cart' })
        .then(function (result) {
          if (result && result.userErrors && result.userErrors.length) {
            status(field, result.userErrors[0].message || labels().error);
            return;
          }
          /**
           * Rewrite the rendered set so a second save sends the right thing
           * without waiting for a refresh. The resolved cart deliberately
           * does not include attributes, so the set submitted is the record.
           */
          var host = document.querySelector('[data-cart-attrs-all]');
          if (host) {
            var next = {};
            attributes.forEach(function (a) {
              next[a.key] = a.value;
            });
            host.textContent = JSON.stringify(next);
          }
          status(field, labels().saved);
        })
        .catch(function () {
          status(field, labels().error);
        })
        .then(function () {
          field.removeAttribute('aria-busy');
        });
    }

    // Debounced: this fires per keystroke otherwise.
    document.addEventListener('input', function (event) {
      var field = event.target.closest && event.target.closest('[data-cart-attr]');
      if (!field) return;
      status(field, '');
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        save(field);
      }, 600);
    });

    // Leaving the field commits immediately rather than waiting out the debounce.
    document.addEventListener(
      'blur',
      function (event) {
        var field = event.target.closest && event.target.closest('[data-cart-attr]');
        if (!field) return;
        if (timer) clearTimeout(timer);
        save(field);
      },
      true
    );

    /**
     * An app can change attributes too. The event carries the complete new
     * set, so the fields are re-rendered from it and the local copy replaced.
     * Own changes are skipped: `context: 'cart'` is what this theme sends.
     */
    document.addEventListener('shopify:cart:attributes-update', function (event) {
      if (!event.attributes) return;
      if (event.context === 'cart') return;

      var next = {};
      event.attributes.forEach(function (a) {
        next[a.key] = a.value;
      });

      var host = document.querySelector('[data-cart-attrs-all]');
      if (host) host.textContent = JSON.stringify(next);

      Array.prototype.forEach.call(document.querySelectorAll('[data-cart-attr]'), function (field) {
        var key = field.getAttribute('data-cart-attr');
        var value = Object.prototype.hasOwnProperty.call(next, key) ? next[key] : '';
        if (document.activeElement !== field) field.value = value;
      });
    });
  }

  /**
   * Cart upsell.
   *
   * Two sources, deliberately in this order:
   *
   *   1. The merchant's metafield picks, already rendered server-side by
   *      snippets/cart-upsell.liquid. A chosen product beats a computed one.
   *   2. Complementary products from the Product Recommendations API, asked
   *      for as `section_id=cart-upsell` so Liquid renders the rows — money
   *      formatting and translations come out right, which they would not if
   *      this built markup from the JSON endpoint.
   *
   * The API caps `limit` at 10 and this asks only for the shortfall, so the
   * two halves together never exceed the row's ceiling.
   *
   * Nothing here is required for the upsell to work: the metafield picks are
   * already on the page before this runs.
   */
  function initCartUpsell() {
    var hosts = document.querySelectorAll('[data-cart-upsell-host]:not([data-upsell-done])');
    if (!hosts.length) return;

    Array.prototype.forEach.call(hosts, function (host) {
      // Marked immediately: the drawer re-renders often, and a second pass
      // would append the same rows again.
      host.dataset.upsellDone = '1';

      var block = host.querySelector('[data-cart-upsell]');
      var list = host.querySelector('[data-cart-upsell-list]');
      if (!block || !list) return;

      var shown = parseInt(block.getAttribute('data-count'), 10) || 0;
      var max = parseInt(block.getAttribute('data-max'), 10) || 10;
      var shortfall = max - shown;
      if (shortfall < 1) return;

      var seed = host.getAttribute('data-seed');
      var url = host.getAttribute('data-url');
      if (!seed || !url) return;

      var query =
        url +
        '?section_id=cart-upsell' +
        '&product_id=' +
        encodeURIComponent(seed) +
        '&intent=' +
        encodeURIComponent(host.getAttribute('data-intent') || 'complementary') +
        '&limit=' +
        shortfall;

      fetch(query)
        .then(function (response) {
          // 404 for an unpublished product, 422 for a bad intent. Either way
          // the metafield picks stand on their own.
          return response.ok ? response.text() : '';
        })
        .then(function (html) {
          if (!html) return;

          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var rows = parsed.querySelectorAll('[data-cart-upsell-rows] > li');
          if (!rows.length) return;

          /**
           * Skip anything the metafield half already offered. The API does
           * not know what those were, so the same product can come back
           * twice over — once chosen, once computed.
           */
          var already = {};
          Array.prototype.forEach.call(list.querySelectorAll('a[href]'), function (link) {
            already[link.getAttribute('href').split('?')[0]] = true;
          });

          var added = 0;
          Array.prototype.forEach.call(rows, function (row) {
            if (shown + added >= max) return;
            var link = row.querySelector('a[href]');
            var href = link && link.getAttribute('href').split('?')[0];
            if (href && already[href]) return;
            list.appendChild(row);
            added++;
          });

          if (added > 0) {
            block.hidden = false;
            // Upsell prices come straight from Liquid too.
            if (window.themeCurrency) window.themeCurrency.reconvert();
          }
        })
        .catch(function () {});
    });
  }

  /**
   * The size chart popup.
   *
   * Same shape as the search dialog: a native <dialog> for the top layer,
   * Escape and focus containment, opened from a link that still goes to the
   * page itself when there is no script.
   */
  function initSizeChart() {
    if (!document.querySelector('[data-sizechart]')) return;
    once('sizeChart', bindSizeChart);
  }

  function bindSizeChart() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest && event.target.closest('[data-sizechart-open]');
      if (opener) {
        // Modified clicks mean "open the page in a tab" — leave them alone.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        // Scoped to this product's own dialog rather than the first on the
        // page, so a template with more than one picker still behaves.
        var scope = opener.closest('.pdp-options') || document;
        var dialog = scope.querySelector('[data-sizechart]') || document.querySelector('[data-sizechart]');
        if (!dialog || typeof dialog.showModal !== 'function') return;

        event.preventDefault();
        dialog.showModal();
        return;
      }

      var closer = event.target.closest && event.target.closest('[data-sizechart-close]');
      if (closer) {
        var open = closer.closest('[data-sizechart]');
        if (open && open.open) open.close();
        return;
      }

      // A click on the dialog itself rather than its panel is the backdrop.
      var el = event.target.closest && event.target.closest('[data-sizechart]');
      if (el && el.open && event.target === el) el.close();
    });
  }

  /* --------------------------------------------------------- drawers */

  function closeDrawer(drawer) {
    if (drawer) drawer.open = false;
  }

  /**
   * Make everything outside the open drawer inert, so Tab cannot wander into
   * the page hidden behind the backdrop. `inert` does the work the browser
   * would do for a modal <dialog>; <details> gets none of that for free.
   */
  function setOutsideInert(drawer) {
    // Release whatever the last open drawer claimed, so this never has to
    // reason about which branch was inert before.
    Array.prototype.forEach.call(document.querySelectorAll('[data-drawer-inert]'), function (node) {
      node.inert = false;
      node.removeAttribute('data-drawer-inert');
    });

    if (!drawer) return;

    /**
     * Walk from the drawer up to <body>, making every sibling inert on the
     * way. Only inerting top-level branches is not enough: the collection
     * filter drawer lives inside <main>, so its own branch is the one
     * holding the product grid, and Tab would walk straight into it behind
     * the backdrop.
     */
    var node = drawer;

    while (node && node !== document.body && node.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, function (sibling) {
        if (sibling === node || sibling.classList.contains('skip-link')) return;
        sibling.inert = true;
        sibling.setAttribute('data-drawer-inert', '');
      });

      node = node.parentElement;
    }
  }

  function initDrawers() {
    var drawers = document.querySelectorAll('[data-drawer]');
    if (!drawers.length) return;

    Array.prototype.forEach.call(drawers, function (drawer) {
      var summary = drawer.querySelector('summary');

      drawer.addEventListener('toggle', function () {
        if (summary) summary.setAttribute('aria-expanded', String(drawer.open));

        if (!drawer.open) {
          setOutsideInert(null);
          return;
        }

        // Only one drawer at a time.
        Array.prototype.forEach.call(drawers, function (other) {
          if (other !== drawer) closeDrawer(other);
        });

        setOutsideInert(drawer);

        // Move focus into the panel so keyboard and screen reader users
        // follow the visual change. The panel itself takes focus rather than
        // its first link, which would otherwise draw a focus ring on a mouse
        // click; Tab from here still walks the panel's contents in order.
        var panel = drawer.querySelector('[data-drawer-panel]');
        if (panel) panel.focus({ preventScroll: true });
      });

      // Click the backdrop to dismiss.
      var overlay = drawer.querySelector('[data-drawer-overlay]');
      if (overlay) {
        overlay.addEventListener('click', function () {
          closeDrawer(drawer);
          if (summary) summary.focus({ preventScroll: true });
        });
      }
    });

    once('drawerEscape', function () {
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;

        var open = document.querySelector('[data-drawer][open]');
        if (!open) return;

        closeDrawer(open);
        var summary = open.querySelector('summary');
        if (summary) summary.focus({ preventScroll: true });
      });
    });
  }

  /* ---------------------------------------------- announcement rotator */

  /**
   * Currency, as chosen in the BUCKS converter app.
   *
   * The app rewrites prices in place. Anything this theme re-renders — the
   * cart drawer, the upsell rows, predictive search — comes back from Liquid
   * in the shop currency, so the conversion has to be run again afterwards.
   * That is the main job here.
   *
   * The app offers the same list twice: a visible <ul> of <li rel="USD">
   * and a hidden <select> of <option value="USD"> for touch devices. Only
   * the first emits a click, so watching clicks alone misses every
   * selection made on a phone.
   *
   * None of this assumes the app is installed. With no app the module reads
   * nothing, converts nothing and costs two idle listeners.
   */
  function initCurrency() {
    once('currency', bindCurrency);
  }

  function bindCurrency() {
    var current = null;

    function stored(key) {
      try {
        var raw = localStorage.getItem(key);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        return parsed && parsed.currency ? parsed.currency : null;
      } catch (error) {
        // Private mode, or the app changed how it stores this.
        return null;
      }
    }

    /**
     * The app renders the active code into its own trigger, which makes it
     * the one source that is right whether the choice was made by hand or
     * by geolocation. Storage is the fallback for the moment before the
     * widget has drawn: a manual pick outranks the detected one.
     */
    function read() {
      var label = document.querySelector('.bucks-selected');
      var shown = label ? label.textContent.trim() : '';
      if (/^[A-Z]{3}$/.test(shown)) return shown;
      return stored('buckscc_last_manual') || stored('buckscc_auto_currency') || null;
    }

    function announce(code) {
      if (!code || code === current) return;
      current = code;

      // A hook for CSS, and for anything that would rather not listen.
      document.documentElement.setAttribute('data-currency', code);
      document.dispatchEvent(new CustomEvent('theme:currency:changed', { detail: { currency: code } }));

      // Prices rendered before the app was ready are still in shop currency.
      reconvert();
    }

    /**
     * Re-run the app over markup this theme has just replaced. `reConvert`
     * re-reads the app's own stored choice, so it needs nothing passed in.
     */
    function reconvert() {
      var app = window.bucksCC;
      if (!app || typeof app.reConvert !== 'function') return;
      try {
        app.reConvert();
      } catch (error) {
        /* The app is mid-render; the next cart change will run it again. */
      }
    }

    /**
     * The app applies the change asynchronously, so the code is read back
     * after it has settled rather than taken from the element clicked —
     * which keeps "Auto Location" honest, since that <li> carries the
     * detected currency rather than the word auto.
     */
    function settle() {
      setTimeout(function () {
        announce(read());

        /**
         * The app converts what is on the page when the choice is made, so
         * this is belt: markup this theme rendered may not be in whatever
         * the app scanned, and running it again is cheap and idempotent.
         */
        reconvert();
      }, 300);
    }

    // The visible list.
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      var item = event.target.closest('.buckscc-select-options .converterTriggers');
      if (!item) return;
      announce(item.getAttribute('rel'));
      settle();
    });

    // The hidden <select> behind it, which never fires a click.
    document.addEventListener('change', function (event) {
      var select = event.target;
      if (!select.matches || !select.matches('#bucksSelector, select.buckcc')) return;
      announce(select.value);
      settle();
    });

    // Cart writes re-render the drawer, the totals and the rewards bar.
    document.addEventListener('theme:cart:updated', reconvert);
    document.addEventListener('shopify:section:load', reconvert);

    window.themeCurrency = {
      get current() {
        return current || read();
      },
      reconvert: reconvert,
    };

    // Storage first: a returning shopper's choice is known immediately,
    // before the widget has drawn, so the page does not start out wrong.
    announce(read());

    /**
     * Then watch the label the app renders.
     *
     * Two things make a one-off read unreliable. The widget is injected by
     * the app, usually after this script has run — and the app can settle on
     * a currency by geolocation entirely on its own, which fires neither a
     * click nor a change. The label is the one signal that covers both.
     */
    var watching = false;

    function watchLabel() {
      var label = document.querySelector('.bucks-selected');
      if (!label) return false;
      if (watching) return true;
      watching = true;

      new MutationObserver(function () {
        announce(read());
      }).observe(label, { childList: true, characterData: true, subtree: true });

      announce(read());

      /**
       * Unconditionally, not just when the code changed. Storage may have
       * already told us the currency before the app had drawn, in which case
       * `announce` sees nothing new and returns — while the prices the app
       * had not yet scanned are still sitting in shop currency.
       */
      reconvert();
      return true;
    }

    if (!watchLabel()) {
      var pending = new MutationObserver(function () {
        if (watchLabel()) pending.disconnect();
      });
      pending.observe(document.documentElement, { childList: true, subtree: true });

      /**
       * Give up eventually. No converter app on this storefront is the
       * normal case, and an observer over the whole document is not
       * something to leave running for a currency that is never coming.
       */
      setTimeout(function () {
        pending.disconnect();
      }, 15000);
    }
  }

  function initAnnouncement() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;

    var all = bar.querySelectorAll('[data-announcement-item]');
    if (!all.length) return;

    var items = [];
    var index = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var timer = null;

    /**
     * Messages the chosen currency is allowed to see.
     *
     * A message with no `data-currencies` belongs to every currency. If the
     * currency is not known — no converter app, or its widget has not drawn
     * yet — every message shows: an empty bar would be a worse answer than
     * one message too many.
     */
    function eligible() {
      var code = (window.themeCurrency && window.themeCurrency.current) || null;

      return Array.prototype.filter.call(all, function (item) {
        // A countdown whose sale ended while the page was open. Liquid
        // already keeps an ended sale out of the markup; this is the same
        // rule applied to a page that was open when the clock ran out.
        if (item.hasAttribute('data-announcement-skip')) return false;

        var only = item.getAttribute('data-currencies');
        if (!only || !code) return true;
        return only.split(',').indexOf(code) !== -1;
      });
    }

    function show(next) {
      if (!items.length) return;
      index = (next + items.length) % items.length;
      items.forEach(function (item, i) {
        item.setAttribute('aria-hidden', String(i !== index));
      });
    }

    function play() {
      // One message has nothing to rotate to.
      if (reduceMotion || timer || items.length < 2) return;
      timer = setInterval(function () {
        show(index + 1);
      }, interval);
    }

    function pause() {
      clearInterval(timer);
      timer = null;
    }

    /**
     * Rebuild the rotation for the current currency. Excluded messages are
     * taken out of the DOM flow rather than just marked hidden, so they
     * cannot be read out or tabbed into while they do not apply.
     */
    function refresh() {
      pause();
      items = eligible();

      Array.prototype.forEach.call(all, function (item) {
        item.hidden = items.indexOf(item) === -1;
      });

      // The navigation is pointless with a single message left.
      Array.prototype.forEach.call(bar.querySelectorAll('[data-announcement-nav]'), function (button) {
        button.hidden = items.length < 2;
      });

      show(0);
      play();
    }

    bar.addEventListener('click', function (event) {
      var button = event.target.closest('[data-announcement-nav]');
      if (!button) return;
      pause();
      show(index + (button.getAttribute('data-announcement-nav') === 'next' ? 1 : -1));
      play();
    });

    // Respect the reader: hold while hovered or focused, and stop entirely
    // when the tab is hidden so we are not burning cycles in the background.
    bar.addEventListener('mouseenter', pause);
    bar.addEventListener('mouseleave', play);
    bar.addEventListener('focusin', pause);
    bar.addEventListener('focusout', play);
    once('announcementVisibility', function () {
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) pause();
        else play();
      });
    });

    // Switching currency changes which messages apply.
    document.addEventListener('theme:currency:changed', refresh);

    // So does a countdown running out.
    document.addEventListener('theme:countdown:expired', refresh);

    refresh();
  }

  /* -------------------------------------------------------- carousels */

  /**
   * Native scroll-snap does the scrolling; JavaScript only reveals the
   * arrow buttons and keeps their disabled state honest.
   */
  /**
   * Drag to pan, for mice only.
   *
   * The tracks are native `overflow-x: auto` scrollers, so touch and trackpad
   * already work and are deliberately left alone — hijacking touch would cost
   * momentum and could swallow vertical page scrolling. A mouse has no such
   * gesture: pressing and dragging selects the text and ghost-drags the
   * product images instead of moving the track, which is what this fixes.
   */
  function bindDrag(track) {
    var startX = 0;
    var startScroll = 0;
    var pointer = null;
    var dragging = false;
    var suppressClick = false;

    // Far enough to be a drag rather than a slightly unsteady click.
    var THRESHOLD = 6;

    function stop() {
      if (pointer !== null && track.hasPointerCapture(pointer)) {
        try {
          track.releasePointerCapture(pointer);
        } catch (error) {
          /* Already gone; nothing to release. */
        }
      }
      pointer = null;

      if (!dragging) return;
      dragging = false;
      // Set here, before the flag is cleared, so the click that follows this
      // pointerup can tell a drag from a tap.
      suppressClick = true;
      track.classList.remove('is-dragging');
      // Handing snapping back re-parks the track on the nearest slide.
      track.style.scrollSnapType = '';
    }

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointer = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      // Deliberately no preventDefault here: that would suppress the
      // compatibility mouse events the links still need for a plain click.
    });

    track.addEventListener('pointermove', function (event) {
      if (pointer === null || event.pointerId !== pointer) return;

      var dx = event.clientX - startX;

      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        track.classList.add('is-dragging');
        // Mandatory snapping fights direct scrollLeft writes, so it is off
        // for the duration of the drag.
        track.style.scrollSnapType = 'none';
        // Capture keeps the moves coming when the cursor leaves the track,
        // but it throws if the pointer is no longer one the element knows
        // about. Unguarded that would abort this handler before the scroll
        // below, leaving the track marked as dragging and refusing to move.
        try {
          track.setPointerCapture(pointer);
        } catch (error) {
          /* Capture is an improvement, not a requirement. */
        }
        // Those first few pixels may have started a selection. Drop it, so no
        // blue highlight is left behind mid-drag.
        var selection = window.getSelection();
        if (selection) selection.removeAllRanges();
      }

      event.preventDefault();
      track.scrollLeft = startScroll - dx;
    });

    track.addEventListener('pointerup', stop);
    track.addEventListener('pointercancel', stop);

    // Images and links are draggable by default, which starts a native drag
    // with a translucent ghost and no scrolling at all.
    track.addEventListener('dragstart', function (event) {
      if (dragging) event.preventDefault();
    });

    // A drag that ends over a product card would otherwise open it. Capture
    // phase, so this runs before the link's own handling.
    track.addEventListener(
      'click',
      function (event) {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /**
   * Endless scrolling for a snap track, by cloning rather than by maths.
   *
   * A full copy of the slides is inserted before and after the real ones, so
   * whatever is centred always has a neighbour on BOTH sides — which is the
   * whole point for the collage, where the first slide used to sit with bare
   * background where its left-hand peek should be.
   *
   * When the shopper drifts into a copy the track jumps by exactly one set.
   * The markup either side is identical, so there is nothing to see.
   *
   * Returns null when there is nothing to loop, and the track then behaves
   * as it always did — swiping still works with no script at all.
   */
  function bindLoop(track) {
    if (track.dataset.loopBound) return null;

    var originals = Array.prototype.slice.call(track.children);
    var count = originals.length;
    if (count < 2) return null;

    track.dataset.loopBound = '1';

    function copySet() {
      var frag = document.createDocumentFragment();

      originals.forEach(function (slide) {
        var copy = slide.cloneNode(true);
        copy.setAttribute('data-carousel-clone', '');

        /**
         * Hidden from assistive tech and out of the tab order. Every one of
         * these is a duplicate link to a collection the real slides already
         * offer, so announcing the list three times over would be worse than
         * not looping at all.
         */
        copy.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(copy.querySelectorAll('a, button, [tabindex]'), function (el) {
          el.setAttribute('tabindex', '-1');
        });

        /**
         * And no `id` survives the copy. A slide may carry a whole product
         * form — the video reel's does — and three forms sharing one id is
         * something `getElementById` cannot tell apart, including Shopify's
         * own scripts. The copies are hidden from assistive tech anyway, so
         * a label that no longer points anywhere costs nothing.
         */
        if (copy.id) copy.removeAttribute('id');
        Array.prototype.forEach.call(copy.querySelectorAll('[id]'), function (el) {
          el.removeAttribute('id');
        });

        frag.appendChild(copy);
      });

      return frag;
    }

    track.insertBefore(copySet(), track.firstChild);
    track.appendChild(copySet());

    // The real slides start after the leading copy.
    var start = count;

    /**
     * The scrollLeft that puts child `i` in the middle of the track.
     *
     * `offsetLeft`/`offsetWidth` rather than `getBoundingClientRect`: the
     * coverflow animation scales the slides, and a scaled rect measures
     * short — which would centre everything slightly off.
     */
    function targetFor(i) {
      var kid = track.children[i];
      if (!kid) return track.scrollLeft;
      return kid.offsetLeft - track.offsetLeft + kid.offsetWidth / 2 - track.clientWidth / 2;
    }

    function setWidth() {
      var first = track.children[0];
      var next = track.children[count];
      return first && next ? next.offsetLeft - first.offsetLeft : 0;
    }

    function normalize() {
      var width = setWidth();
      if (!width) return;

      var base = targetFor(start);
      var here = track.scrollLeft;

      if (here < base - 1) track.scrollLeft = here + width;
      else if (here >= base + width - 1) track.scrollLeft = here - width;
    }

    /**
     * Only once the scroll has stopped. Moving scrollLeft mid-gesture kills
     * momentum on iOS and cancels an arrow's smooth scroll half way, and one
     * spare set either side is plenty of runway to wait.
     */
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', normalize);
    } else {
      var settling = null;
      track.addEventListener(
        'scroll',
        function () {
          clearTimeout(settling);
          settling = setTimeout(normalize, 140);
        },
        { passive: true }
      );
    }

    // Start on the first real slide rather than in the leading copy.
    track.scrollLeft = targetFor(start);

    return {
      count: count,
      start: start,
      targetFor: targetFor,
      normalize: normalize,
    };
  }

  function initCarousels() {
    var carousels = document.querySelectorAll('[data-carousel]');

    Array.prototype.forEach.call(carousels, function (carousel) {
      // The theme editor re-runs init on every section reload; without this
      // each reload would stack another set of click and scroll listeners.
      if (carousel.dataset.carouselReady) return;

      var track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;

      /**
       * Watch the track so the gate below gets a second chance.
       *
       * It used to run exactly once, at load. A carousel that was not
       * scrollable then — a narrow window, or few enough slides that they all
       * fit — had its arrows left hidden and its drag unbound, and widening
       * the window never revived it, because the resize listener that would
       * have re-measured is itself only bound *after* the gate passes.
       *
       * Re-entering `initCarousels` is safe: the `carouselReady` flag above
       * stops anything binding twice.
       */
      if (!carousel.dataset.carouselWatched && typeof ResizeObserver === 'function') {
        carousel.dataset.carouselWatched = '1';
        new ResizeObserver(function () {
          if (!carousel.dataset.carouselReady) initCarousels();
        }).observe(track);
      }

      var buttons = carousel.querySelectorAll('[data-carousel-nav]');
      var dotWrap = carousel.querySelector('[data-carousel-dots]');
      var dots = dotWrap ? Array.prototype.slice.call(dotWrap.querySelectorAll('[data-carousel-dot]')) : [];

      /**
       * Before the gate below and before the slides are collected: cloning is
       * what makes the track scrollable in the first place, and the copies
       * have to be part of `slides` for the dot maths to line up.
       */
      var loop = track.hasAttribute('data-carousel-loop') ? bindLoop(track) : null;

      // Nothing to page through — leave the arrows hidden and drop the dots,
      // which would otherwise sit there doing nothing.
      if (track.scrollWidth <= track.clientWidth + 4) {
        if (dotWrap) dotWrap.hidden = true;
        return;
      }

      carousel.dataset.carouselReady = '1';

      // The gate hides the dots when there is nothing to page through. If it
      // failed earlier and passes now, they have to come back.
      if (dotWrap) dotWrap.hidden = false;

      // Drag applies whether or not this carousel has arrows or dots, so it
      // is bound before the arrows-and-dots check below bails out.
      bindDrag(track);

      if (!buttons.length && !dots.length) return;

      var slides = Array.prototype.slice.call(track.children);

      /**
       * Slides may snap to their start or to their centre depending on the
       * section, so both the dot maths and the dot jumps read the alignment
       * off the slide rather than assuming one.
       */
      function centred(slide) {
        return (getComputedStyle(slide).scrollSnapAlign || '').indexOf('center') > -1;
      }

      // Rects rather than offsetLeft: the track and its slides do not always
      // share an offsetParent.
      function targetFor(slide) {
        var delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
        var target = track.scrollLeft + delta;
        if (centred(slide)) target -= (track.clientWidth - slide.clientWidth) / 2;
        return target;
      }

      function currentIndex() {
        var tr = track.getBoundingClientRect();
        var best = 0;
        var bestDistance = Infinity;

        slides.forEach(function (slide, i) {
          var r = slide.getBoundingClientRect();
          var mid = centred(slide);
          var point = mid ? r.left + r.width / 2 : r.left;
          var reference = mid ? tr.left + tr.width / 2 : tr.left;
          var distance = Math.abs(point - reference);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        });

        return best;
      }

      Array.prototype.forEach.call(buttons, function (button) {
        button.hidden = false;

        button.addEventListener('click', function () {
          // Measure the real distance between two slides rather than assuming
          // a gap — `--space-gap` is fluid, so it is not a fixed number.
          // `Math.abs`, because in a right-to-left track the second slide
          // sits to the LEFT of the first and the raw difference is negative.
          var kids = track.children;
          var step =
            kids.length > 1
              ? Math.abs(kids[1].getBoundingClientRect().left - kids[0].getBoundingClientRect().left)
              : track.clientWidth * 0.8;

          /**
           * Right-to-left scrollers count backwards: `scrollLeft` starts at 0
           * at the trailing edge and goes negative. So "next" has to scroll by
           * a negative amount, or the arrows page the wrong way round in
           * Arabic and Hebrew.
           */
          if (getComputedStyle(track).direction === 'rtl') step = -step;

          track.scrollBy({
            left: button.getAttribute('data-carousel-nav') === 'next' ? step : -step,
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        });
      });

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          // A dot means the i-th REAL slide, which sits past the leading copy.
          var slide = slides[loop ? loop.start + i : i];
          if (!slide) return;
          track.scrollTo({
            left: targetFor(slide),
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        });
      });

      var raf = null;

      function sync() {
        raf = null;

        /**
         * A looping track has no ends, so neither arrow is ever spent.
         *
         * Distance travelled, not raw `scrollLeft`: right-to-left tracks run
         * from 0 down to negative, so the raw value would report a fresh RTL
         * track as already at its end and disable the wrong arrow.
         */
        if (loop) {
          Array.prototype.forEach.call(buttons, function (button) {
            button.disabled = false;
          });
        } else {
          var max = track.scrollWidth - track.clientWidth - 2;
          var travelled = Math.abs(track.scrollLeft);
          Array.prototype.forEach.call(buttons, function (button) {
            var next = button.getAttribute('data-carousel-nav') === 'next';
            button.disabled = next ? travelled >= max : travelled <= 2;
          });
        }

        if (!dots.length) return;

        // Fold the copies back onto the real slides they stand in for.
        var active = currentIndex();
        if (loop) active = ((active - loop.start) % loop.count + loop.count) % loop.count;
        dots.forEach(function (dot, i) {
          var on = i === active;
          dot.classList.toggle('is-current', on);
          dot.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      track.addEventListener(
        'scroll',
        function () {
          if (raf === null) raf = requestAnimationFrame(sync);
        },
        { passive: true }
      );

      // Slide widths are percentages, so the reachable range changes with the
      // viewport. Re-check when it does.
      addEventListener('resize', function () {
        if (raf === null) raf = requestAnimationFrame(sync);
      }, { passive: true });

      sync();
    });
  }

  /* --------------------------------------------------- reveal fallback */

  /**
   * Browsers with scroll-driven animations handle reveals in CSS. This is
   * the fallback for the rest, and it disconnects as it goes.
   */
  function initReveal() {
    if (reduceMotion) return;
    if (CSS.supports('animation-timeline', 'view()')) return;

    var targets = document.querySelectorAll('.js-reveal');
    if (!targets.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.animate(
            [
              { opacity: 0, transform: 'translateY(1.25rem)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 520, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'both' }
          );
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );

    Array.prototype.forEach.call(targets, function (target) {
      observer.observe(target);
    });
  }

  /* ------------------------------------------------- quantity stepper */

  /**
   * Reveals the minus/plus buttons on the product form and wires them to the
   * number input. They ship `hidden`, so with no JavaScript the input keeps
   * its native spinners and the form still works — the same bargain the
   * carousel arrows make.
   */
  function initQuantity() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-qty-input]'), function (input) {
      var box = input.parentElement;
      if (!box) return;

      Array.prototype.forEach.call(box.querySelectorAll('[data-qty-step]'), function (button) {
        button.hidden = false;

        button.addEventListener('click', function () {
          var step = parseInt(button.getAttribute('data-qty-step'), 10) || 1;
          var min = parseInt(input.getAttribute('min'), 10);
          var max = parseInt(input.getAttribute('max'), 10);
          var next = (parseInt(input.value, 10) || 1) + step;

          if (!isNaN(min)) next = Math.max(min, next);
          if (!isNaN(max)) next = Math.min(max, next);

          if (String(next) === input.value) return;

          input.value = next;
          // So anything listening for a quantity change hears about it.
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* -------------------------------------------------------- cart drawer */

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Whether Razorpay's Magic Cart is live for this visitor.
   *
   * Razorpay injects `#sidecart-js` only once its own config has the cart
   * switched on and any A/B test it runs has put this visitor in the Magic
   * Cart group. From then on it opens its own panel on the bag click and
   * after every request to /cart/add — so opening this drawer as well
   * stacked two carts on top of each other.
   *
   * Asked at the moment of use, never cached at load: Razorpay's script is
   * deferred behind this one and injects the tag later.
   */
  function razorpayCart() {
    return !!document.getElementById('sidecart-js');
  }

  /**
   * Whether this drawer must stay shut: the merchant chose Razorpay's cart
   * in the Cart drawer section, which renders only a stub carrying
   * `data-cart-external`, or Razorpay's cart is live regardless.
   */
  function externalCart() {
    return !!document.querySelector('[data-cart-external]') || razorpayCart();
  }

  /**
   * Whether Razorpay's cart is on its way, even if it has not loaded yet:
   * its config block is on the page and has the cart switched on. The same
   * JSON Razorpay reads itself.
   */
  function razorpayComing() {
    if (razorpayCart()) return true;

    var el = document.getElementById('RazorpayMagicSidecartConfig');
    if (!el) return false;

    try {
      var data = JSON.parse(el.textContent);
      return !!(data && data.config && data.config.isEnabled);
    } catch (e) {
      return false;
    }
  }

  /**
   * `fetch` as it was when this file ran, before Razorpay's script — deferred
   * behind this one — wraps it. Razorpay only sees an add made through its
   * wrapper, so a mismatch is how an add tells whether Razorpay caught it.
   */
  var pageFetch = window.fetch;

  /**
   * The cart drawer, and the Ajax add that makes it worth having.
   *
   * Everything here is an upgrade over markup that already works: the bag is
   * a link to the cart page, the drawer's own form posts to /cart, and the
   * product forms post to /cart/add. With no script all three still do their
   * job — the drawer simply never opens.
   */
  function initCartDrawer() {
    if (!document.querySelector('[data-cart-drawer]')) return;

    // Every listener in there is delegated on the document, so this binds
    // once per page no matter how often `init` runs.
    once('cartDrawer', bindCartDrawer);
  }

  /**
   * Announce a cart change this theme made itself.
   *
   * Deliberately a theme-specific event, not one of Shopify's standard cart
   * events: those are emitted by `Shopify.actions.updateCart`, and dispatching
   * one here as well would show every listening app the same change twice.
   * rewards-bar.js listens for this to re-check its gift tiers.
   */
  function announceCartChange() {
    document.dispatchEvent(new CustomEvent('theme:cart:updated'));
  }

  function bindCartDrawer() {
    var lastOpener = null;

    /**
     * Looked up on demand rather than captured: the theme editor can replace
     * this section's markup outright, and a captured reference would be a
     * detached node from then on.
     */
    function root() {
      return document.querySelector('[data-cart-drawer]');
    }

    function panel() {
      var el = root();
      return el && el.querySelector('[data-cart-drawer-panel]');
    }

    function open(opener) {
      // Razorpay opens its own panel in response to the same add or click.
      if (externalCart()) return;

      var el = root();
      if (!el) return;

      lastOpener = opener || document.activeElement;
      el.hidden = false;
      // Next frame, so the transition has a state to animate from.
      requestAnimationFrame(function () {
        el.classList.add('is-open');
      });
      setOutsideInert(el);
      document.documentElement.style.overflow = 'hidden';

      var box = panel();
      if (box) box.focus({ preventScroll: true });
    }

    /**
     * Hand the cart to Razorpay, waiting for it if it is still loading.
     *
     * Razorpay takes a click on the bag in the capture phase, so the toggle
     * handler only ever sees one it passed on. The usual reason is timing:
     * Razorpay attaches its click listener only once its own panel has
     * loaded, which on a slow phone is seconds after the page. Following the
     * link then dropped the shopper on the cart page for nothing, so the
     * click is replayed every 250ms until Razorpay takes it.
     *
     * The cart page is the last resort, for when Razorpay's cart is not
     * coming: its config is missing or switched off, or five seconds pass —
     * an A/B test holding this visitor back, or a blocked script.
     */
    var handOffUntil = 0;
    var handOffTimer = null;
    var replaying = false;

    function handToRazorpay(bag) {
      if (!replaying) handOffUntil = Date.now() + 5000;
      clearTimeout(handOffTimer);

      if (!bag || !razorpayComing() || Date.now() > handOffUntil) {
        window.location.href = bag ? bag.href : shopRoot() + 'cart';
        return;
      }

      handOffTimer = setTimeout(function () {
        replaying = true;
        bag.click();
        replaying = false;
      }, 250);
    }

    function close() {
      var el = root();
      if (!el || el.hidden) return;

      el.classList.remove('is-open');
      setOutsideInert(null);
      document.documentElement.style.overflow = '';

      // Hide only once it has slid out, or it would vanish instantly.
      var delay = reduceMotion ? 0 : 320;
      setTimeout(function () {
        if (!el.classList.contains('is-open')) el.hidden = true;
      }, delay);

      if (lastOpener && lastOpener.isConnected) lastOpener.focus({ preventScroll: true });
    }

    /**
     * Swap freshly rendered drawer markup in.
     *
     * Split out from `refresh` because the add-to-cart path gets this same
     * HTML back inside its own response and must not fetch it a second time.
     */
    function swap(html) {
      if (!html) return;

      var el = root();
      if (!el) return;

      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('[data-cart-drawer]');
      if (!fresh) return;

      var wasOpen = el.classList.contains('is-open');
      el.innerHTML = fresh.innerHTML;
      if (wasOpen) el.classList.add('is-open');

      /**
       * The section rendered the new count, so the badge costs no request.
       *
       * The attribute only exists in an up-to-date cart-drawer.liquid, and
       * the add response carries the line item rather than the cart — so
       * without it there is nothing to count, and the old extra round trip
       * is the fallback rather than a stale badge.
       */
      var count = fresh.getAttribute('data-cart-item-count');

      if (count !== null) {
        updateCartCount(parseInt(count, 10) || 0);
      } else {
        fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (cart) {
            if (cart) updateCartCount(cart.item_count);
          })
          .catch(function () {});
      }

      // The steppers in the new markup need revealing again, and the
      // upsell host is brand new, so its recommendations must be
      // re-fetched — the seed product may well have changed.
      initQuantity();
      initCartUpsell();

      /**
       * Every price in here has just come back from Liquid in the shop
       * currency. Opening the drawer goes through this same path, so
       * without re-converting the drawer shows rupees to a shopper who
       * chose dollars.
       */
      if (window.themeCurrency) window.themeCurrency.reconvert();
    }

    /**
     * Re-render this section alone through the Section Rendering API and swap
     * its markup in. Delegated listeners live on `document`, so replacing the
     * insides costs nothing — nothing was bound to them.
     */
    function refresh(thenOpen, opener) {
      return fetch(shopRoot() + '?sections=cart-drawer', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          if (data) swap(data['cart-drawer']);
        })
        .catch(function () {})
        .then(function () {
          if (thenOpen) open(opener);
        });
    }

    /**
     * Removing a line. The link works on its own, so this only has to stop
     * the navigation and do the same thing in place.
     *
     * `/cart/change.js` takes the line key as `id`, which is why the key is
     * on the element rather than dug out of the href.
     */
    document.addEventListener('click', function (event) {
      var remove = event.target.closest && event.target.closest('[data-cart-remove]');
      if (!remove) return;

      // Modified clicks mean "open this in a tab" — leave them to the browser.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      var key = remove.getAttribute('data-cart-remove');
      if (!key) return;

      event.preventDefault();
      remove.setAttribute('aria-busy', 'true');

      fetch(shopRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // The re-rendered drawer rides back with the removal.
        body: JSON.stringify({ id: key, quantity: 0, sections: 'cart-drawer' }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('remove failed');
          return response.json();
        })
        .then(function (cart) {
          swap(cart.sections && cart.sections['cart-drawer']);
          updateCartCount(cart.item_count);
          announceCartChange();
        })
        .catch(function () {
          // Server-side trouble: let the link do what it would have done.
          window.location.href = remove.href;
        });
    });

    /**
     * Published for snippets/standard-actions.liquid, which configures
     * Shopify's standard `openCart` and `updateCart` actions to drive this
     * drawer instead of reloading the page or navigating to /cart.
     *
     * Deliberately narrow: two functions, no state. Anything richer would be
     * an API this theme then has to keep.
     */
    window.themeCart = {
      open: function () {
        /**
         * Razorpay's panel has no public opener, but it claims any click on
         * the bag link. If it is not ready for one yet, the click falls
         * through to the toggle handler below, which waits for it.
         */
        if (externalCart()) {
          var bag = document.querySelector('[data-cart-toggle]');
          if (bag) {
            bag.click();
          } else {
            handToRazorpay(null);
          }
          return Promise.resolve();
        }

        // Same reasoning as the bag: show it now, reconcile behind it.
        open(document.activeElement);
        return refresh(false);
      },
      refresh: function () {
        return refresh(false);
      },
    };

    // Delegated, so it keeps working after the drawer's markup is replaced.
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-cart-toggle]');
      if (toggle) {
        // Leave modified clicks alone — they mean "open the cart page".
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();

        // Razorpay passed on this click; give it until it is ready.
        if (externalCart()) {
          handToRazorpay(toggle);
          return;
        }

        /**
         * Open first, re-render after.
         *
         * This used to fetch the drawer section and only open once the
         * response came back, so every tap on the bag waited out a round
         * trip. The markup on the page is already the current cart — the
         * server rendered it, and every Ajax change since has refreshed it —
         * so there is nothing to wait for. The refresh behind it only catches
         * a cart changed somewhere this page never saw, such as another tab.
         */
        open(toggle);
        refresh(false);
        return;
      }

      if (event.target.closest('[data-cart-drawer-close], [data-cart-drawer-overlay]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var el = root();
      if (el && !el.hidden) close();
    });

    /**
     * Add to cart without leaving the page.
     *
     * `defaultPrevented` is the guard that matters: the variant-picker check
     * and the video reel's own Ajax add both call `preventDefault` on this
     * same event from listeners closer to the form, so bailing here is what
     * stops a second, duplicate add.
     */
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (event.defaultPrevented) return;
      if (!form.matches || !form.matches('form[action$="/cart/add"]')) return;

      event.preventDefault();

      var button = form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');

      var body = new FormData(form);

      /**
       * Ask for the re-rendered drawer in the SAME response.
       *
       * This used to be three round trips in a row — add, then fetch the
       * drawer section, then fetch /cart.js for the badge — and the drawer
       * did not open until the middle one came back. The add endpoint will
       * render sections for us, and the section carries the new item count,
       * so one request now does all three jobs.
       */
      body.append('sections', 'cart-drawer');

      /**
       * Open straight away rather than when the response lands. The drawer
       * already shows the current cart, so there is something correct to
       * look at immediately and the new line drops in behind it.
       */
      open(button);

      // Read before the request: it is this call that Razorpay has to see.
      var seenByRazorpay = razorpayCart() && window.fetch !== pageFetch;

      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function (data) {
          swap(data && data.sections && data.sections['cart-drawer']);
          announceCartChange();

          /**
           * Razorpay opens its panel by itself after an add it saw. One made
           * before it had loaded went straight past it, so it is handed the
           * bag click instead — the same wait as tapping the bag.
           */
          if (externalCart() && !seenByRazorpay) {
            handToRazorpay(document.querySelector('[data-cart-toggle]'));
          }
        })
        .catch(function () {
          // Something went wrong server-side — let the real form post handle
          // it rather than leaving the shopper with a button that did nothing.
          form.submit();
        })
        .then(function () {
          if (button) button.removeAttribute('aria-busy');
        });
    });

    /**
     * Quantity changes inside the drawer go over Ajax — reloading the page
     * underneath would close the drawer the shopper is looking at. Zero is
     * how Shopify removes a line, which is why the input's `min` is 0.
     */
    var pendingQty = null;

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('[data-qty-input]')) return;
      if (!input.closest('[data-cart-drawer]')) return;

      // Coalesce a burst of stepper clicks into one request.
      clearTimeout(pendingQty);
      pendingQty = setTimeout(function () {
        var el = root();
        if (!el) return;

        var updates = {};
        Array.prototype.forEach.call(el.querySelectorAll('[data-qty-input]'), function (field) {
          var key = (field.name.match(/^updates\[(.+)\]$/) || [])[1];
          if (key) updates[key] = parseInt(field.value, 10) || 0;
        });

        fetch(shopRoot() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, sections: 'cart-drawer' }),
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (cart) {
            if (!cart) return;
            swap(cart.sections && cart.sections['cart-drawer']);
            updateCartCount(cart.item_count);
            // A quantity change is the likeliest way to cross a reward
            // threshold, so this path has to announce like the others.
            announceCartChange();
          })
          .catch(function () {});
      }, 400);
    });

    // The video reel does its own add and announces it; keep in step.
    document.addEventListener('cart:updated', function () {
      refresh(false);
    });
  }

  /** Keeps the header bag badge honest without re-rendering the header. */
  function updateCartCount(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (node) {
      var before = parseInt(node.textContent, 10) || 0;

      node.textContent = count;
      node.hidden = count === 0;

      // Only on the way up: removing a line should not be celebrated.
      if (count <= before) return;

      /**
       * Restart the animation on a repeat add. Removing the class is not
       * enough on its own — the browser coalesces the remove and the add into
       * one style change and nothing replays, so the layout is read back in
       * between to force the class change to take effect.
       */
      node.classList.remove('is-bumped');
      void node.offsetWidth;
      node.classList.add('is-bumped');
    });
  }

  /**
   * Keep the badge's visibility following its number.
   *
   * Razorpay's cart writes the new count into the badge after a change made
   * in its own panel — it finds it by the `cart-count-bubble` class — but
   * writes only the number. Without this an emptied cart left a visible "0",
   * and the first add to an empty cart left the badge hidden.
   */
  function initCartCountSync() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]:not([data-count-sync])'), function (node) {
      node.setAttribute('data-count-sync', '');

      new MutationObserver(function () {
        var empty = (parseInt(node.textContent, 10) || 0) === 0;
        if (node.hidden !== empty) node.hidden = empty;
      }).observe(node, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ------------------------------------------------------- cart updates */

  /**
   * The cart posts to /cart with `updates[]`, so changing a quantity is a
   * normal form submit and works with no JavaScript — there is a visible
   * Update button inside <noscript> for exactly that case.
   *
   * All this does is remove the second step: change a quantity and the form
   * goes. Setting it to zero is how a line is removed, which is why the
   * input's `min` is 0 rather than 1.
   */
  function initCartForm() {
    /**
     * Every cart form, not just the first — the drawer carries one too, and
     * it sits above `main` in the document, so a `querySelector` would bind
     * to the drawer's and leave the cart page's quantities dead.
     *
     * The drawer's own form is skipped here: it updates over Ajax from
     * `initCartDrawer` instead of reloading the page underneath it.
     */
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-form]'), function (form) {
      if (form.closest('[data-cart-drawer]')) return;
      if (form.dataset.cartFormReady) return;
      form.dataset.cartFormReady = '1';

      var pending = null;

      form.addEventListener('change', function (event) {
        if (!event.target.matches('[data-qty-input]')) return;

        // Coalesce a burst of stepper clicks into one reload.
        clearTimeout(pending);
        pending = setTimeout(function () {
          form.requestSubmit
            ? form.requestSubmit()
            : form.submit();
        }, 400);
      });
    });
  }

  /* ----------------------------------------------------- sticky buy bar */

  /**
   * Reveals the product page's sticky buy bar once the inline buy row has
   * scrolled off the top. Same 1px sentinel trick as the header, so there is
   * no scroll listener and nothing on the main thread per frame.
   */
  /**
   * The sticky buy bar shows whenever the real Add to cart is NOT on screen.
   *
   * That covers both directions from one test: on arrival the row is below
   * the fold — on a phone the gallery alone fills the screen — so the bar is
   * there from the start; scrolling down to the row hides it, because two Add
   * to cart buttons at once is one too many; scrolling past brings it back.
   *
   * The earlier version watched a sentinel for "has this scrolled above the
   * viewport", which is only half the question and left the bar hidden for
   * the whole of the first screen.
   */
  function initStickyBuy() {
    var bar = document.querySelector('[data-sticky-buy]');
    var anchor = document.querySelector('[data-sticky-buy-anchor]');
    if (!bar || !anchor) return;

    if (bar.dataset.stickyBound) return;
    bar.dataset.stickyBound = '1';

    // Default root and a zero threshold: intersecting means any part of the
    // row is in the viewport. The observer fires once on setup, so the bar
    // is correct before the shopper touches anything.
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }).observe(anchor);
  }

  /**
   * Choosing a sold-out size swaps the buy row for the WhatsApp enquiry.
   *
   * The sizes are all selectable — a shopper whose size is gone can say so
   * rather than finding a dead button — and what the page offers changes to
   * match: Add to cart and Buy Now step aside, the enquiry takes their place,
   * and the message carries the size they picked.
   *
   * With no script every size is still selectable and the enquiry link is
   * already rendered with a working href, so nothing here is load-bearing.
   */
  function initSoldOutSwap() {
    /**
     * Gated on the size chips, not on the enquiry link.
     *
     * It used to bail out when there was no WhatsApp button — so a shop with
     * no number set got no swap at all, and picking a sold-out size left Add
     * to cart sitting there ready to fail. Hiding it is right regardless;
     * the enquiry is the nicer half, not the whole point.
     */
    if (!document.querySelector('input[data-variant-available]')) return;
    once('soldOutSwap', bindSoldOutSwap);
  }

  function bindSoldOutSwap() {
    /**
     * Every control exists twice — once in the inline buy row and once in
     * the sticky bar — so each of these is a list. A `querySelector` here
     * updated the inline row and quietly left the sticky bar showing a
     * button for a variant that cannot be bought.
     */
    function apply(scope, available, title) {
      var adds = scope.querySelectorAll('[data-pdp-add]');
      var buyNows = scope.querySelectorAll('[data-pdp-buy-now]');
      var enquires = scope.querySelectorAll('[data-pdp-enquire]');
      var notices = scope.querySelectorAll('[data-pdp-soldout]');

      function toggle(list, hidden) {
        Array.prototype.forEach.call(list, function (el) {
          el.hidden = hidden;
        });
      }

      toggle(adds, !available);
      toggle(buyNows, !available);

      // One of the two takes the buy row's place, never both.
      toggle(enquires, available);
      toggle(notices, available || enquires.length > 0);

      if (available || !enquires.length) return;

      /**
       * Rebuild each link so the message names the size. The number and the
       * base message are rendered as data by the section, so this never has
       * to parse the href it is replacing.
       */
      Array.prototype.forEach.call(enquires, function (enquire) {
        var number = enquire.getAttribute('data-wa-number');
        var message = enquire.getAttribute('data-wa-message') || '';
        if (!number) return;

        if (title) message = message + ' (' + title + ')';
        enquire.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(message);
      });
    }

    /**
     * Hiding the buttons is not enough on its own.
     *
     * Implicit submission still fires from the quantity field — press Enter
     * there with a sold-out size chosen and the form posts a variant that
     * cannot be bought, which comes back as a Shopify error page. This stops
     * it at the source, whatever route the submit came from.
     */
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form.matches || !form.matches('form[action$="/cart/add"]')) return;

        /**
         * The chips themselves, not `form.elements.id`. They are renamed out
         * of the `id` group once there is script to keep a hidden field in
         * step with them, so the name is no longer what identifies them —
         * `data-variant-available` is, and it is on every one of them
         * whichever name they are wearing.
         */
        var chosen =
          Array.prototype.filter.call(
            form.querySelectorAll('input[data-variant-available]'),
            function (el) {
              return el.checked;
            }
          )[0] || null;

        if (!chosen) return;
        if (chosen.getAttribute('data-variant-available') !== 'false') return;

        event.preventDefault();
        event.stopPropagation();

        // Send them where the button would have: the enquiry.
        var enquire = document.querySelector('[data-pdp-enquire]');
        if (enquire && !enquire.hidden) enquire.focus();
      },
      // Capture, so this runs before the Ajax add handler further down.
      true
    );

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('input[data-variant-available]')) return;

      /**
       * Scoped to the buy controls this picker belongs to.
       *
       * It used to reach across the whole document, which was right while a
       * page could only ever show one product: the inline buy row and the
       * sticky bar are two halves of the same thing and both have to move
       * together. A quick-view popup is a second product on the same page,
       * and picking a size in it would otherwise have hidden Add to cart on
       * the product underneath.
       */
      var scope = input.closest('[data-buy-scope]') || document;

      // The attribute is Liquid's boolean, so it is the string 'true'.
      apply(
        scope,
        input.getAttribute('data-variant-available') === 'true',
        input.getAttribute('data-variant-title')
      );
    });
  }

  /* ------------------------------------------------------- variant swap */

  /**
   * Change variant without reloading the page.
   *
   * The colour and size chips are links to `?variant=<id>`, which is what
   * makes them work with no script at all — and what made every colour
   * change a full page load, scrolling the shopper back to the top of a
   * product they were halfway down.
   *
   * This fetches the product section for the chosen variant and swaps it in.
   * Liquid still renders it, so prices, the sale badge, availability, the
   * sold-out swap and the gallery's colour ordering all come out of the
   * server exactly as they would on a real load — none of it is rebuilt in
   * the browser, and `money` is never reimplemented here.
   *
   * The link is left alone on a modified click and on any failure, so the
   * worst case is the navigation that would have happened anyway.
   */
  function initVariantSwap() {
    if (!document.querySelector('[data-variant-link]')) return;
    once('variantSwap', bindVariantSwap);
  }

  function bindVariantSwap() {
    var controller = null;

    /** The section wrapper Shopify renders around the product section. */
    function sectionOf(node) {
      return node.closest('[id^="shopify-section-"]');
    }

    function swap(link) {
      var url = link.getAttribute('href');
      var host = sectionOf(link);
      if (!url || !host) return false;

      var id = host.id.replace('shopify-section-', '');

      /**
       * Marked busy rather than blanked. The markup on screen is still the
       * product, just the previous variant of it, so leaving it up and
       * dimming it reads better than a gap where the page was.
       */
      host.setAttribute('aria-busy', 'true');

      // One request at a time: a slower earlier response landing after a
      // later one would leave the page showing a variant nobody chose.
      if (controller) controller.abort();
      controller = new AbortController();

      fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(id), {
        signal: controller.signal,
        cache: 'no-store',
      })
        .then(function (response) {
          if (!response.ok) throw new Error('variant swap failed');
          return response.text();
        })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild;
          if (!fresh) throw new Error('variant swap empty');

          host.innerHTML = fresh.innerHTML;
          host.removeAttribute('aria-busy');

          /**
           * The address bar follows, so a refresh or a shared link lands on
           * the variant being looked at. `replaceState`, not `pushState`:
           * back should leave the product, not walk the shopper through
           * every colour they tried on the way.
           */
          if (window.history && history.replaceState) history.replaceState({}, '', url);

          // Bind the carousels, the variant field and the buy guards in the
          // markup that has just arrived. `init` is the theme's own idiom
          // for this and every part of it is guarded against binding twice.
          init();
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          host.removeAttribute('aria-busy');
          // Do what the link says rather than leaving a dead chip.
          window.location.href = url;
        });

      return true;
    }

    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-variant-link]');
      if (!link) return;

      /**
       * A chip inside the quick-view popup belongs to the popup, which
       * re-fetches itself. This swaps a product SECTION, and the popup is
       * not inside one — so the lookup below would find nothing, the
       * click would go unhandled, and the chip would navigate away.
       */
      if (link.closest('.qv-modal')) return;

      // Modified clicks mean "open in a tab" — leave them alone.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      if (swap(link)) event.preventDefault();
    });
  }

  /* ---------------------------------------------------------- quick view */

  /**
   * The quick-view popup.
   *
   * The `+` on a product card is a link to the product page. Where the
   * product has one variant the card adds it to the cart outright; where it
   * has options there is a size to pick first, and this is what turns that
   * trip into a popup rather than a page load.
   *
   * The contents are Liquid, fetched on demand from the product's own URL
   * with `?view=quick-view` — a template that renders with `layout none`, so
   * the response is the popup's markup and nothing else. Prices, money
   * formatting, translations and variant availability all come out of the
   * server, and none of it is downloaded until a shopper asks for it.
   *
   * The link is left alone on a modified click, and on any failure: it is a
   * real link to a real page, so the worst case is the navigation that would
   * have happened anyway.
   */
  function initQuickView() {
    if (!document.querySelector('[data-quickview]')) return;
    once('quickView', bindQuickView);
  }

  function bindQuickView() {
    var dialog = null;
    var panel = null;
    var opener = null;
    var controller = null;
    var loaded = {};

    function build() {
      if (dialog) return dialog;

      dialog = document.createElement('dialog');
      dialog.className = 'qv-modal';
      dialog.setAttribute('scroll-lock', '');
      dialog.setAttribute('aria-label', document.title);

      panel = document.createElement('div');
      panel.className = 'qv-modal__panel';
      dialog.appendChild(panel);

      /**
       * No close button is built here. It arrives with the fetched markup,
       * where Liquid gives it a label from the locale file — a button this
       * script created would have had to carry an English default.
       *
       * While the fetch is in flight there is nothing to close but Escape
       * and the backdrop, both of which the dialog handles itself.
       */
      document.body.appendChild(dialog);

      /**
       * Focus goes back where it came from. The browser does this for a
       * dialog it opened from a button, but the popup is opened from a link
       * whose default was prevented, so it is this code's job.
       */
      dialog.addEventListener('close', function () {
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
        opener = null;
      });

      return dialog;
    }

    function fill(html) {
      // Parsed rather than assigned, so a malformed response fails here
      // instead of half-rendering into the page.
      var fresh = new DOMParser().parseFromString(html, 'text/html');
      var content = fresh.querySelector('.qv');
      if (!content) return false;

      panel.innerHTML = '';
      panel.appendChild(document.importNode(content, true));
      panel.removeAttribute('aria-busy');

      var title = panel.querySelector('.qv__title');
      if (title) dialog.setAttribute('aria-label', title.textContent.trim());

      /**
       * Bind everything in the new markup: the gallery carousel, the single
       * variant field the accelerated checkout button needs, and the two
       * guards that keep a sizeless or sold-out variant out of the cart.
       *
       * `init` is the theme's own idiom for markup that has just arrived —
       * the theme editor re-runs it on every section reload — and every part
       * of it is guarded against binding twice.
       */
      init();
      return true;
    }

    /**
     * A skeleton of the real thing, not a spinner in a short box.
     *
     * The popup opens before the fetch lands, so whatever is drawn first
     * decides its size. A small placeholder meant it opened as a white
     * sliver and jumped to full height a moment later. This carries the
     * same class as the content that replaces it, so it inherits the same
     * two-column grid, and its image block carries the same aspect ratio —
     * which is most of the height. What arrives then barely moves it.
     */
    function skeleton() {
      return (
        '<div class="qv qv--loading" aria-hidden="true">' +
        '<div class="qv__media"><span class="qv__skel qv__skel--media"></span></div>' +
        '<div class="qv__body">' +
        '<span class="qv__skel qv__skel--line" style="width:70%"></span>' +
        '<span class="qv__skel qv__skel--line" style="width:40%"></span>' +
        '<span class="qv__skel qv__skel--line qv__skel--gap"></span>' +
        '<span class="qv__skel qv__skel--line" style="width:85%"></span>' +
        '<span class="qv__skel qv__skel--line qv__skel--gap" style="width:30%"></span>' +
        '<span class="qv__skel qv__skel--chips"></span>' +
        '<span class="qv__skel qv__skel--button"></span>' +
        '</div></div>'
      );
    }

    /**
     * Fetch a product's quick view and put it in the panel.
     *
     * Shared by opening the popup and by changing variant inside it — the
     * difference between those is only what is on screen while it loads, so
     * the request, the cache and the give-up-and-navigate path all live here.
     */
    function load(url) {
      if (loaded[url]) {
        fill(loaded[url]);
        return;
      }

      // One request at a time: a slower earlier response landing after a
      // later one would fill the popup with the wrong product.
      if (controller) controller.abort();
      controller = new AbortController();

      /**
       * `no-store` matters more here than on the page swap next door: this
       * one is a full page render, and a product URL goes through the shop's
       * page cache. A Section Rendering API call is dynamic by nature; a
       * cached page can come back as the same HTML whatever `?variant=` says.
       */
      fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=quick-view', {
        signal: controller.signal,
        cache: 'no-store',
      })
        .then(function (response) {
          if (!response.ok) throw new Error('quick view failed');
          return response.text();
        })
        .then(function (html) {
          if (!fill(html)) throw new Error('quick view empty');
          loaded[url] = html;
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          /**
           * Give up and do what the link says. The shopper asked to see this
           * product; a broken popup must not be the end of that.
           */
          if (dialog.open) dialog.close();
          window.location.href = url;
        });
    }

    function open(link) {
      var url = link.getAttribute('href');
      if (!url) return false;

      build();
      opener = link;

      // Drawn before the fetch: a popup that appears at once and fills in
      // reads as faster than a link that hangs and then opens.
      panel.innerHTML = skeleton();

      if (typeof dialog.showModal !== 'function') return false;
      if (!dialog.open) dialog.showModal();

      load(url);
      return true;
    }

    /**
     * A size or colour chip inside the popup.
     *
     * The chips are the same picker the product page uses, so they are links
     * to `?variant=<id>` — and the page's own variant swap cannot help here,
     * because it re-renders a product SECTION and the popup is not inside
     * one. Left alone they did what the href says and navigated away, which
     * is the opposite of what a popup is for.
     *
     * Kept dimmed rather than replaced with the skeleton: the shopper is
     * looking at this product and only one thing about it is changing.
     */
    function change(link) {
      var url = link.getAttribute('href');
      if (!url || !dialog || !dialog.open) return false;

      panel.setAttribute('aria-busy', 'true');
      load(url);
      return true;
    }

    document.addEventListener('click', function (event) {
      var closer = event.target.closest && event.target.closest('[data-quickview-close]');
      if (closer) {
        if (dialog && dialog.open) dialog.close();
        return;
      }

      // A click on the dialog itself rather than its panel is the backdrop.
      if (dialog && dialog.open && event.target === dialog) {
        dialog.close();
        return;
      }

      // Modified clicks mean "open the product in a tab" — leave them alone.
      var plain = !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);
      if (!plain) return;

      var chip = event.target.closest && event.target.closest('[data-variant-link]');
      if (chip && dialog && dialog.contains(chip)) {
        if (change(chip)) event.preventDefault();
        return;
      }

      var link = event.target.closest && event.target.closest('[data-quickview]');
      if (!link) return;

      if (open(link)) event.preventDefault();
    });

    /**
     * Adding from the popup opens the cart drawer over it, which would leave
     * two stacked dialogs and the drawer behind the popup's own backdrop.
     */
    document.addEventListener('theme:cart:updated', function () {
      if (dialog && dialog.open) dialog.close();
    });
  }

  /* ------------------------------------------------------ variant field */

  /**
   * Gives the radio-chip form a real `id` field.
   *
   * The chips themselves cannot be it. They are `required` so that a form
   * posted with nothing chosen is rejected without JavaScript — but that
   * same `required` blocks the submit event this theme needs in order to
   * show its own message, and the radios are visually hidden, so the
   * browser cannot even anchor its bubble to them. Nothing happens, and the
   * shopper is left pressing a dead button.
   *
   * So once JavaScript is running the chips are renamed and pointed at a
   * form that does not exist, which detaches them from this form's
   * validation entirely, and a hidden `id` carries the chosen value instead.
   * The no-script behaviour is untouched, because none of this happens
   * without a script.
   */
  function initVariantField() {
    Array.prototype.forEach.call(document.querySelectorAll('form[action$="/cart/add"]'), function (form) {
      var chips = form.querySelectorAll('input[type="radio"][data-variant-available]');
      if (!chips.length || form.dataset.variantField) return;
      form.dataset.variantField = 'on';

      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'id';
      hidden.setAttribute('data-variant-field', '');
      form.insertBefore(hidden, form.firstChild);

      Array.prototype.forEach.call(chips, function (chip) {
        chip.name = 'pdp-variant';
        chip.setAttribute('form', 'pdp-variant-none');
        if (chip.checked) hidden.value = chip.value;
      });

      /**
       * `bubbles`, because the listeners that care about the chosen variant
       * — the sold-out swap, the sticky bar — are delegated on the document
       * rather than bound to this field.
       */
      form.addEventListener('change', function (event) {
        var chip = event.target;
        if (!chip.matches || !chip.matches('input[data-variant-available]')) return;
        hidden.value = chip.value;
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* --------------------------------------------------- required options */

  /**
   * "Please select a size", instead of a button that does nothing.
   *
   * Only the radio path has chips to guard, so a product on the link picker
   * returns early — there the form already carries a real variant id.
   *
   * `noValidate` is what lets the submit event fire at all: see the note in
   * `initVariantField` about why the browser's own validation is no use on
   * a visually hidden radio.
   */
  function initRequiredOptions() {
    Array.prototype.forEach.call(document.querySelectorAll('form[action$="/cart/add"]'), function (form) {
      var chips = form.querySelectorAll('input[data-variant-available]');
      if (!chips.length || form.dataset.optionGuard) return;
      form.dataset.optionGuard = 'on';

      var group = form.querySelector('.pdp-options');
      var message = group ? group.querySelector('[data-option-error]') : null;

      form.noValidate = true;

      function chosen() {
        return (
          Array.prototype.filter.call(chips, function (chip) {
            return chip.checked;
          })[0] || null
        );
      }

      function prompt() {
        if (message) message.hidden = false;
        if (!group) return;

        group.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

        var first = group.querySelector('input[data-variant-available]:not(:disabled)');
        if (first) first.focus({ preventScroll: true });
      }

      form.addEventListener('submit', function (event) {
        if (chosen()) return;
        event.preventDefault();
        prompt();
      });

      /**
       * Buy Now needs its own guard, on the capture phase. Shopify's script
       * reads the form and goes straight to checkout without firing a submit
       * event, so the handler above never runs for it — and with no chip
       * chosen the form carries no `id` at all, which lands the shopper on
       * "This item is no longer available" rather than at a checkout.
       */
      var buyNow = form.querySelector('[data-pdp-buy-now]');
      if (buyNow) {
        buyNow.addEventListener(
          'click',
          function (event) {
            if (chosen()) return;
            event.preventDefault();
            event.stopPropagation();
            prompt();
          },
          true
        );
      }

      // Choosing a size answers the message, so it goes away on its own.
      if (group && message) {
        group.addEventListener('change', function () {
          if (chosen()) message.hidden = true;
        });
      }
    });
  }

  /* ----------------------------------------------------------- product zoom */

  /**
   * The full-screen image viewer: tap to magnify, drag to pan, swipe or
   * arrow between images.
   *
   * Delegated on the document and bound once, so it survives the gallery
   * being re-rendered and costs nothing on a page with no viewer.
   */
  function initProductZoom() {
    if (!document.querySelector('[data-pdp-zoom]')) return;
    once('productZoom', bindProductZoom);
  }

  function bindProductZoom() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target.closest) return;

      var opener = target.closest('[data-zoom-open]');
      if (opener) {
        openZoom(opener);
        return;
      }

      var closer = target.closest('[data-zoom-close]');
      if (closer) {
        var owner = closer.closest('[data-pdp-zoom]');
        if (owner && owner.open) owner.close();
        return;
      }

      // A click on the dialog itself, which is the backdrop around the image.
      var dialog = target.closest('[data-pdp-zoom]');
      if (dialog && dialog.open && target === dialog) dialog.close();
    });
  }

  /**
   * Wire one viewer, once. The controller is cached against the dialog so
   * re-opening it does not stack a second set of listeners.
   */
  function bindZoomDialog(dialog) {
    var existing = zoomControllers.get(dialog);
    if (existing) return existing;

    var track = dialog.querySelector('[data-zoom-track]');
    if (!track) return null;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-zoom-slide]'));
    var toggle = dialog.querySelector('[data-zoom-toggle]');
    var counter = dialog.querySelector('[data-zoom-count]');

    var zoomed = null;
    var panX = 0;
    var panY = 0;

    function imageOf(slide) {
      return slide.querySelector('.pdp-zoom__img');
    }

    /**
     * The size the picture is actually drawn at, which is not the slide's
     * size: `object-fit: contain` letterboxes it. The pan limits have to
     * stop at the edge of the picture, not of its padding.
     */
    function painted(slide) {
      var image = imageOf(slide);
      var box = { w: slide.clientWidth, h: slide.clientHeight };
      if (!image || !image.naturalWidth || !image.naturalHeight) return box;

      var fit = Math.min(box.w / image.naturalWidth, box.h / image.naturalHeight);
      return { w: image.naturalWidth * fit, h: image.naturalHeight * fit };
    }

    function limits(slide) {
      var size = painted(slide);
      return {
        x: Math.max(0, (size.w * ZOOM_SCALE - slide.clientWidth) / 2),
        y: Math.max(0, (size.h * ZOOM_SCALE - slide.clientHeight) / 2),
      };
    }

    function clamp(value, bound) {
      return Math.min(bound, Math.max(-bound, value));
    }

    function paint() {
      slides.forEach(function (slide) {
        var image = imageOf(slide);
        if (!image) return;
        image.style.transform =
          slide === zoomed
            ? 'translate(' + panX + 'px, ' + panY + 'px) scale(' + ZOOM_SCALE + ')'
            : '';
      });

      dialog.classList.toggle('is-zoomed', zoomed !== null);

      if (!toggle) return;
      var label = zoomed ? toggle.dataset.labelOut : toggle.dataset.labelIn;
      toggle.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      if (label) toggle.setAttribute('aria-label', label);
    }

    function reset() {
      if (zoomed === null) return;
      zoomed = null;
      panX = 0;
      panY = 0;
      paint();
    }

    /**
     * Magnify, aimed at the point that was tapped. The transform origin
     * stays centred and the offset does the aiming, so the maths is the same
     * whichever part of the picture was hit.
     */
    function zoomInto(slide, point) {
      var image = slide && imageOf(slide);
      if (!image) return;

      zoomed = slide;
      panX = 0;
      panY = 0;

      if (point) {
        var box = image.getBoundingClientRect();
        var bound = limits(slide);
        panX = clamp(-(point.x - (box.left + box.width / 2)) * ZOOM_SCALE, bound.x);
        panY = clamp(-(point.y - (box.top + box.height / 2)) * ZOOM_SCALE, bound.y);
      }

      paint();
    }

    function targetFor(slide) {
      var delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
      return track.scrollLeft + delta;
    }

    function currentIndex() {
      var box = track.getBoundingClientRect();
      var reference = box.left + box.width / 2;
      var best = 0;
      var bestDistance = Infinity;

      slides.forEach(function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - reference);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });

      return best;
    }

    // The neighbours either side, so a swipe never lands on a blank slide.
    function preload(index) {
      [index - 1, index, index + 1].forEach(function (i) {
        var slide = slides[i];
        var image = slide && imageOf(slide);
        if (!image || image.loading !== 'lazy') return;
        image.loading = 'eager';
        if (i === index) image.fetchPriority = 'high';
      });
    }

    function sync() {
      var index = currentIndex();
      // Swiping away from a magnified image drops the magnification.
      if (zoomed && zoomed !== slides[index]) reset();
      preload(index);
      if (counter && counter.dataset.template) {
        counter.textContent = counter.dataset.template.replace('[index]', String(index + 1));
      }
    }

    var raf = null;
    track.addEventListener(
      'scroll',
      function () {
        if (raf !== null) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          sync();
        });
      },
      { passive: true }
    );

    /* ------------------------------------------------------------- pan */

    var pointer = null;
    var startX = 0;
    var startY = 0;
    var fromX = 0;
    var fromY = 0;
    var panning = false;
    var swallowClick = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.button !== 0) return;
      var slide = event.target.closest && event.target.closest('[data-zoom-slide]');
      if (!slide || slide !== zoomed) return;

      pointer = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      fromX = panX;
      fromY = panY;
    });

    track.addEventListener('pointermove', function (event) {
      if (pointer === null || event.pointerId !== pointer) return;

      var dx = event.clientX - startX;
      var dy = event.clientY - startY;

      // Far enough to be a drag rather than a slightly unsteady tap.
      if (!panning) {
        if (Math.abs(dx) < PAN_THRESHOLD && Math.abs(dy) < PAN_THRESHOLD) return;
        panning = true;
        dialog.classList.add('is-panning');
        try {
          track.setPointerCapture(pointer);
        } catch (error) {
          /* capture is an optimisation, not a requirement */
        }
        var selection = window.getSelection();
        if (selection) selection.removeAllRanges();
      }

      event.preventDefault();

      var bound = limits(zoomed);
      panX = clamp(fromX + dx, bound.x);
      panY = clamp(fromY + dy, bound.y);
      paint();
    });

    function endPan() {
      if (pointer !== null && track.hasPointerCapture(pointer)) {
        try {
          track.releasePointerCapture(pointer);
        } catch (error) {
          /* nothing to do */
        }
      }
      pointer = null;
      if (!panning) return;
      panning = false;
      dialog.classList.remove('is-panning');
      // The click that ends a drag must not also toggle the zoom.
      swallowClick = true;
    }

    track.addEventListener('pointerup', endPan);
    track.addEventListener('pointercancel', endPan);
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    track.addEventListener('click', function (event) {
      if (swallowClick) {
        swallowClick = false;
        return;
      }
      var slide = event.target.closest && event.target.closest('[data-zoom-slide]');
      if (!slide) return;
      if (slide === zoomed) reset();
      else zoomInto(slide, { x: event.clientX, y: event.clientY });
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (zoomed) reset();
        else zoomInto(slides[currentIndex()], null);
      });
    }

    /* ---------------------------------------------------------- paging */

    function step(next) {
      reset();

      var stride =
        slides.length > 1
          ? Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left)
          : track.clientWidth;

      if (getComputedStyle(track).direction === 'rtl') stride = -stride;

      track.scrollBy({ left: next ? stride : -stride, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    Array.prototype.forEach.call(dialog.querySelectorAll('[data-zoom-nav]'), function (button) {
      button.addEventListener('click', function () {
        step(button.getAttribute('data-zoom-nav') === 'next');
      });
    });

    dialog.addEventListener('keydown', function (event) {
      if (slides.length < 2) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      step(event.key === 'ArrowRight');
    });

    dialog.addEventListener('close', reset);

    var controller = {
      show: function (index) {
        reset();
        preload(index);
        var slide = slides[index];
        // `auto`: the viewer has just opened, so there is nothing to animate from.
        if (slide) track.scrollTo({ left: targetFor(slide), behavior: 'auto' });
        sync();
      },
    };

    zoomControllers.set(dialog, controller);
    return controller;
  }

  /**
   * Open the viewer belonging to the gallery this opener sits in, on the
   * image that was clicked.
   *
   * Scoped to the product rather than to the page, so a template showing two
   * products cannot open the wrong one's images.
   */
  function openZoom(opener) {
    var scope = opener.closest('.pdp') || document;
    var dialog = scope.querySelector('[data-pdp-zoom]') || document.querySelector('[data-pdp-zoom]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    var controller = bindZoomDialog(dialog);
    if (!controller) return;

    /**
     * Shown before the track is measured: a closed dialog is `display: none`,
     * so its track has no width to scroll and every measurement inside it
     * would come back zero.
     */
    dialog.showModal();
    controller.show(parseInt(opener.getAttribute('data-zoom-open'), 10) || 0);
  }

  /* --------------------------------------------------- sale countdown */

  /**
   * Keeps the campaign countdowns ticking.
   *
   * Liquid renders the real remaining time into the digits, so this is only
   * ever correcting a number that is already right — which is why nothing
   * here has to run before paint, and why a page served from cache still
   * shows a sensible countdown if the script never arrives.
   *
   * One interval for every clock on the page rather than one each: the
   * announcement bar, the sale banner and the product offer can all be
   * showing at once, and three timers drifting apart would have them
   * disagreeing by a second in plain sight.
   *
   * The elements are re-queried on each tick instead of being captured once,
   * so a section the theme editor swaps out is picked up without rebinding,
   * and one that is removed simply stops being found.
   */
  function initCountdown() {
    if (!document.querySelector('[data-countdown]')) return;
    once('countdown', bindCountdown);
  }

  function bindCountdown() {
    var timer = null;

    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    /**
     * Write only on change. A countdown touches four nodes a second on
     * however many clocks are on screen, and an unconditional write is an
     * unconditional style recalculation.
     */
    function set(root, selector, value) {
      var node = root.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    }

    /**
     * The sale ran out while the page was open.
     *
     * The clock is removed rather than zeroed: "ends in 00d 00h 00m" is a
     * worse statement than no countdown at all. An announcement slide is
     * marked first so the rotator can drop it before its contents vanish —
     * otherwise the bar rotates to a blank line.
     */
    function expire(clock) {
      var slide = clock.closest('[data-announcement-item]');
      if (slide) slide.setAttribute('data-announcement-skip', '');

      clock.remove();
      document.dispatchEvent(new CustomEvent('theme:countdown:expired'));
    }

    function render(clock, now) {
      var start = parseInt(clock.getAttribute('data-countdown-start'), 10) || 0;
      var end = parseInt(clock.getAttribute('data-countdown-end'), 10) || 0;

      if (now >= end) {
        expire(clock);
        return;
      }

      /**
       * Before the sale opens the clock runs to the opening — the teaser
       * week — and swaps itself over to the end date at the moment the sale
       * starts, without a reload. A shopper sitting on the page as the sale
       * opens is exactly the shopper this is for.
       */
      var toStart = start > now;
      var target = toStart ? start : end;
      var label = clock.getAttribute(
        toStart ? 'data-countdown-label-starts' : 'data-countdown-label-ends'
      );

      set(clock, '[data-countdown-text]', label);

      var left = Math.max(0, target - now);
      var days = Math.floor(left / 86400);
      var hours = Math.floor((left % 86400) / 3600);
      var minutes = Math.floor((left % 3600) / 60);

      set(clock, '[data-countdown-days]', String(days));
      set(clock, '[data-countdown-hours]', pad(hours));
      set(clock, '[data-countdown-minutes]', pad(minutes));
      set(clock, '[data-countdown-seconds]', pad(left % 60));

      /**
       * The live region, rewritten once a minute rather than once a second.
       * A polite region that changes every second is read continuously and
       * makes the rest of the page unusable with a screen reader on.
       */
      if (clock.getAttribute('data-announced-at') === String(minutes)) return;
      clock.setAttribute('data-announced-at', String(minutes));

      var announce = clock.querySelector('[data-countdown-announce]');
      if (!announce) return;

      // The unit suffixes are already in the DOM in the shop's language, so
      // they are read back off it rather than duplicated into the script.
      var units = clock.querySelectorAll('.countdown__unit');

      function unit(index) {
        return units[index] ? units[index].textContent : '';
      }

      announce.textContent =
        label + ' ' + days + unit(0) + ' ' + hours + unit(1) + ' ' + minutes + unit(2);
    }

    function tick() {
      var clocks = document.querySelectorAll('[data-countdown]');

      if (!clocks.length) {
        stop();
        return;
      }

      var now = Math.floor(Date.now() / 1000);

      Array.prototype.forEach.call(clocks, function (clock) {
        render(clock, now);
      });
    }

    function start() {
      if (timer) return;
      timer = setInterval(tick, 1000);
      tick();
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    // Nothing to count while the tab is in the background, and the digits
    // are resynced from the clock the moment it comes back.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    start();
  }

  /* ------------------------------------------------------ arriving timer */

  /**
   * The "coming soon" marquee.
   *
   * Two jobs, and neither is rendering the strip: the section already ships
   * the words and a correct clock, so with no JavaScript it is a static,
   * accurate statement that simply does not scroll.
   *
   *   1. Repeat the unit until it covers the viewport, then duplicate the
   *      whole run once — the `-50%` keyframe is what makes that seamless,
   *      and it only works if the two halves are identical.
   *   2. Keep the clock ticking.
   *
   * Kept apart from `initCountdown` deliberately. That one drives the
   * campaign countdown from the shop's sale dates into split d/h/m/s parts;
   * this has its own target per section and prints a single string, because
   * a marquee is one line of type. Sharing a ticker between them would mean
   * one function branching on output format in two directions.
   */
  function initArrivingTimer() {
    if (!document.querySelector('[data-arriving-timer]')) return;

    // Filling is per element and idempotent, so it is safe to re-run when
    // the theme editor swaps a section's markup.
    Array.prototype.forEach.call(document.querySelectorAll('[data-arriving-timer]'), fillArrivingTimer);

    once('arrivingTimer', bindArrivingTimer);
  }

  function fillArrivingTimer(strip) {
    var track = strip.querySelector('[data-arriving-track]');
    var unit = strip.querySelector('[data-arriving-unit]');
    if (!track || !unit) return;

    var unitHTML = unit.outerHTML;
    var runHTML = unitHTML;

    track.innerHTML = runHTML;

    /**
     * Guarded, because the exit condition is a measurement: a strip that is
     * hidden, or in a tab that has not been painted, reports zero width for
     * both sides and the comparison never becomes false.
     */
    var guard = 0;
    while (track.scrollWidth < strip.offsetWidth && guard < 50) {
      runHTML += unitHTML;
      track.innerHTML = runHTML;
      guard++;
    }

    track.innerHTML = runHTML + runHTML;
  }

  function bindArrivingTimer() {
    var timer = null;

    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function plural(value, word) {
      return value + ' ' + word + (value === 1 ? '' : 's');
    }

    function announcePrefix(strip, rest) {
      var prefix = strip.getAttribute('data-announce-prefix');
      return prefix ? prefix + ': ' + rest : rest;
    }

    function render(strip, now) {
      var target = parseInt(strip.getAttribute('data-target'), 10) || 0;
      var clocks = strip.querySelectorAll('[data-arriving-clock]');
      var announce = strip.querySelector('[data-arriving-announce]');
      var left = target - now;
      var i;

      /**
       * Arrived. The clock becomes the announcement rather than sitting at
       * 00:00:00 for the rest of the day, and this strip stops being
       * counted — `data-target` is cleared so the next tick skips it.
       */
      if (left <= 0) {
        var live = strip.getAttribute('data-live-text') || '';
        for (i = 0; i < clocks.length; i++) clocks[i].textContent = live;
        if (announce) announce.textContent = announcePrefix(strip, live);
        strip.removeAttribute('data-target');
        return;
      }

      var days = Math.floor(left / 86400);
      var hours = Math.floor((left % 86400) / 3600);
      var minutes = Math.floor((left % 3600) / 60);

      var text = pad(hours) + ':' + pad(minutes) + ':' + pad(left % 60);
      if (days > 0) text = days + (strip.getAttribute('data-day-unit') || '') + ' ' + text;

      for (i = 0; i < clocks.length; i++) {
        if (clocks[i].textContent !== text) clocks[i].textContent = text;
      }

      /**
       * The live region, rewritten once a minute rather than once a second.
       * A polite region that changes every second is read continuously and
       * makes the rest of the page unusable with a screen reader on.
       */
      if (!announce) return;

      if (strip.getAttribute('data-announced-at') === String(minutes)) return;
      strip.setAttribute('data-announced-at', String(minutes));

      announce.textContent = announcePrefix(
        strip,
        (days > 0 ? plural(days, 'day') + ' ' : '') +
          plural(hours, 'hour') + ' ' + plural(minutes, 'minute') + ' remaining'
      );
    }

    function tick() {
      // Re-queried each second rather than captured once, so a section the
      // theme editor reloads is picked up without rebinding, and a strip
      // that has arrived drops out on its own.
      var strips = document.querySelectorAll('[data-arriving-timer][data-target]');

      if (!strips.length) {
        stop();
        return;
      }

      var now = Math.floor(Date.now() / 1000);
      Array.prototype.forEach.call(strips, function (strip) {
        render(strip, now);
      });
    }

    function start() {
      if (timer) return;
      timer = setInterval(tick, 1000);
      tick();
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    /**
     * Rotating a phone widens the strip, and the run built for the old width
     * no longer covers it — which shows as a gap part-way through the loop.
     */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-arriving-timer]'),
          fillArrivingTimer
        );
        tick();
      }, 200);
    });

    start();
  }

  /* ------------------------------------------------------- coupon code */

  /**
   * Copy the discount code.
   *
   * The button ships hidden and is revealed here, because the clipboard API
   * is undefined on an insecure origin — a copy button that silently does
   * nothing is worse than none, and the code is selectable text either way.
   */
  function initCoupon() {
    var buttons = document.querySelectorAll('[data-coupon-copy]');
    if (!buttons.length || !navigator.clipboard) return;

    Array.prototype.forEach.call(buttons, function (button) {
      button.hidden = false;
    });

    once('coupon', function () {
      document.addEventListener('click', function (event) {
        var button = event.target.closest('[data-coupon-copy]');
        if (!button) return;

        var chip = button.closest('.coupon');
        var code = chip && chip.querySelector('[data-coupon-code]');
        var label = button.querySelector('[data-coupon-copy-text]');
        if (!code || !label) return;

        navigator.clipboard.writeText(code.textContent.trim()).then(function () {
          // Restored afterwards, so the button does not become a permanent
          // "Copied" with no way to copy again.
          var original = button.getAttribute('data-original-label') || label.textContent;
          button.setAttribute('data-original-label', original);

          label.textContent = button.getAttribute('data-copied-label');
          button.classList.add('is-copied');

          clearTimeout(restoreTimers.get(button));
          restoreTimers.set(
            button,
            setTimeout(function () {
              label.textContent = original;
              button.classList.remove('is-copied');
            }, 2000)
          );
        });
      });
    });
  }

  /* ------------------------------------------------- currency selector */

  function initSelectors() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-form-select]'), function (select) {
      select.addEventListener('change', function () {
        select.form.submit();
      });
    });
  }

  /* ------------------------------------------------------------- boot */

  function init() {
    initStickyHeader();
    initScrollLock();
    initDrawers();
    initSearchModal();
    initSizeChart();
    initCurrency();
    initAnnouncement();
    // After the bar: a countdown that has already run out marks its slide,
    // and the rotator has to exist to be told about it.
    initCountdown();
    initCoupon();
    initArrivingTimer();
    initCarousels();
    initQuantity();
    initCartForm();
    initCartDrawer();
    initCartCountSync();
    initCartAttributes();
    initCartUpsell();
    initStickyBuy();
    // Before the two guards below: both read the chips, and this is what
    // decides whether the chips or a hidden field are the form's `id`.
    initVariantSwap();
    initQuickView();
    initVariantField();
    initRequiredOptions();
    initSoldOutSwap();
    initProductZoom();
    initReveal();
    initSelectors();
  }

  init();

  // Re-run when the theme editor swaps a section's markup.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
